// src/components/Dashboard/DashboardContents/GeneralList.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Typography, Box, Paper, Chip, IconButton, Button, Alert, TableCell, TableSortLabel, FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, CheckCircle as CheckCircleIcon, ErrorOutline as ErrorOutlineIcon, Search as SearchIcon, ArrowForward as BackIcon } from '@mui/icons-material';
import useFetch from '../../../../hooks/useFetch';
import useDeleteItem from '../../../../hooks/useDeleteItem';
import RTL from '../../../../utils/RTL';
import StandardTable from '../../../common/table/StandardTable';
import { toPersianDigits } from '../../../../utils/convertNumbers';
import { formatJalaliDate } from '../../../../utils/formatJalaliDate';
import UserForm from './UserForm';
import ConfirmationDialog from '../ConfirmationDialog';
import FloatingSnackbar from '../../../common/FloatingSnackbar';
import api from '../../../../config/axiosConfig';
import { getNestedValue } from '../../../../utils/getNestedValue';
import TablePagination from './TablePagination';

// Constants - moved outside component to avoid re-creation
const PROFILE_ONLY_FIELDS = new Set(['role', 'image']);

// Shared height so the toolbar controls (search, status, action) line up.
const FIELD_HEIGHT = 40;

// Helper: return a clean path (works with absolute/relative), ensures trailing slash
const normalizeBasePath = (urlLike) => {
    if (!urlLike) return null;
    try {
        // absolute
        const u = new URL(urlLike);
        return u.pathname.endsWith('/') ? u.pathname : `${u.pathname}/`;
    } catch {
        // relative
        const path = urlLike.startsWith('http')
            ? urlLike // some weird absolute that new URL failed on
            : urlLike; // relative like '/accounts/api/v1/profile/students/'
        const withSlash = path.endsWith('/') ? path : `${path}/`;
        // If a full http URL was passed, strip origin part so axios baseURL is used
        if (withSlash.startsWith('http')) {
            try {
                return new URL(withSlash).pathname.replace(/\/+$/, '/') // single trailing slash
            } catch {
                return withSlash; // last resort
            }
        }
        return withSlash;
    }
};

// Remove undefined/null/empty-string values; keep 0/false
const pruneEmpty = (obj) => {
    const out = {};
    Object.entries(obj || {}).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (typeof v === 'string' && v.trim() === '') return;
        out[k] = v;
    });
    return out;
};

export default function GeneralList({
    fetchUrl,
    submitUrl,               // may be undefined; we’ll default to fetchUrl
    createDataFunction,
    rowHeaders,
    editFields,
    displayFields,
    secondaryFields = [],    // display field names that fold away when space is tight
    title = 'فهرست کاربران',
    addLabel = 'افزودن کاربر',
    icon = null,
}) {
    const [rowsPerPage, setRowsPerPage] = useState(15);

    const [items, setItems] = useState([]);
    const [page, setPage] = useState(0);

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedTerm, setDebouncedTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('active'); // active | inactive | all

    const [selectedRowId, setSelectedRowId] = useState(null);
    // Full-page add/edit form: null = list view, 'new' = add, <id> = edit that user.
    const [formTarget, setFormTarget] = useState(null);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const [refreshToken, setRefreshToken] = useState(0);
    const [orderBy, setOrderBy] = useState('full_name'); // Default sort by name (last name)
    const [orderDirection, setOrderDirection] = useState('asc'); // 'asc' or 'desc'

    const { deleteItem, isLoading: isDeleting } = useDeleteItem();

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedTerm(searchTerm);
            setPage(0);
        }, 500);
        return () => clearTimeout(t);
    }, [searchTerm]);

    // Map frontend field names to backend ordering field names
    const getOrderingField = (fieldName) => {
        const fieldMap = {
            'full_name': 'profile__user__last_name',
            'first_name': 'profile__user__first_name',
            'last_name': 'profile__user__last_name',
            'email': 'profile__user__email',
            'mobile': 'profile__user__mobile',
            'national_id': 'profile__user__national_id',
            'birth_date': 'profile__user__birth_date',
            'created_date': 'profile__user__created_date',
            'address': 'profile__user__address',
        };
        return fieldMap[fieldName] || fieldName;
    };

    // Memoize search query to avoid recreating on every render
    const searchQuery = useMemo(() => {
        const orderingField = getOrderingField(orderBy);
        const ordering = orderDirection === 'desc' ? `-${orderingField}` : orderingField;
        const status = statusFilter ? `status=${encodeURIComponent(statusFilter)}&` : '';
        return `${debouncedTerm ? `search=${encodeURIComponent(debouncedTerm)}&` : ''}${status}page=${page + 1}&page_size=${rowsPerPage}&ordering=${ordering}`;
    }, [debouncedTerm, page, rowsPerPage, orderBy, orderDirection, statusFilter]);

    const { data: paginatedResponse, loading, error, refetch } = useFetch(`${fetchUrl}?${searchQuery}`);

    useEffect(() => {
        if (paginatedResponse) setItems(paginatedResponse.results || []);
    }, [paginatedResponse]);

    // Refetch when refreshToken changes
    useEffect(() => {
        if (refreshToken > 0) {
            refetch();
        }
    }, [refreshToken, refetch]);

    // Clear selection when pagination or sorting changes
    useEffect(() => {
        setSelectedRowId(null);
    }, [page, rowsPerPage, orderBy, orderDirection]);

    // Handle sort
    const handleSort = useCallback((fieldName) => {
        if (orderBy === fieldName) {
            // Toggle direction if same field
            setOrderDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            // New field, default to ascending
            setOrderBy(fieldName);
            setOrderDirection('asc');
        }
        setPage(0); // Reset to first page when sorting changes
    }, [orderBy]);


    // Pagination handlers - memoized with useCallback
    const handleChangePage = useCallback((newPage) => {
        setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to first page when changing page size
    }, []);

    const closeForm = useCallback(() => {
        setFormTarget(null);
        setSelectedRowId(null);
    }, []);

    // Add → full-page empty form.
    const handleAdd = useCallback(() => {
        setSelectedRowId(null);
        setFormTarget('new');
    }, []);

    // Edit / row click → full-page form for that user.
    const handleEdit = useCallback((user) => {
        setSelectedRowId(user.id);
        setFormTarget(user.id);
    }, []);
    const handleRowClick = handleEdit;

    const handleDelete = useCallback((id) => {
        setItemToDelete(id);
        setOpenDeleteDialog(true);
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!itemToDelete) return;
        try {
            await deleteItem(`${fetchUrl}${itemToDelete}/`);
            setSnackbar({ open: true, message: 'مورد با موفقیت حذف شد.', severity: 'success' });
            if (formTarget === itemToDelete) {
                closeForm();
            }
            setRefreshToken(prev => prev + 1);
            setTimeout(() => {
                refetch();
            }, 100);
        } catch (err) {
            setSnackbar({
                open: true,
                message: err?.response?.data?.detail || 'خطا در حذف مورد',
                severity: 'error'
            });
        } finally {
            setOpenDeleteDialog(false);
            setItemToDelete(null);
        }
    }, [itemToDelete, fetchUrl, deleteItem, formTarget, closeForm, refetch]);

    // Handle form success
    const handleFormSuccess = useCallback(() => {
        const wasEdit = formTarget !== 'new';
        closeForm();
        setRefreshToken(prev => prev + 1);
        setTimeout(() => {
            refetch();
        }, 100);
        setSnackbar({
            open: true,
            message: wasEdit ? 'کاربر با موفقیت ویرایش شد.' : 'کاربر با موفقیت ثبت شد.',
            severity: 'success'
        });
    }, [formTarget, closeForm, refetch]);

    // Memoize rows and columns to avoid recreating on every render
    const rows = useMemo(() => {
        return items.map((item) => createDataFunction(item));
    }, [items, createDataFunction]);

    // Full-page form state. Edit target is recomputed from the live rows so it
    // stays fresh; falls back to the list if the row is deleted/filtered out.
    const isAdding = formTarget === 'new';
    const formUser = useMemo(
        () => (formTarget != null && !isAdding ? rows.find((r) => r.id === formTarget) || null : null),
        [rows, formTarget, isAdding]
    );
    const showForm = isAdding || formUser != null;

    // Determine if a field is sortable
    const isSortableField = (fieldName) => {
        const sortableFields = ['full_name', 'first_name', 'last_name', 'email', 'mobile', 'national_id', 'birth_date', 'created_date', 'address'];
        return sortableFields.includes(fieldName);
    };

    const columns = useMemo(() => {
        const fieldColumns = displayFields.map((field, idx) => {
            const label = rowHeaders[idx] || field;
            const sortable = isSortableField(field);
            const isCurrentlySorted = orderBy === field;
            
            return {
                id: field,
                label,
                sortable,
                align: 'center',
                priority: secondaryFields.includes(field) ? 'secondary' : undefined,
                renderHeader: sortable ? (
                    <TableSortLabel
                        active={isCurrentlySorted}
                        direction={isCurrentlySorted ? orderDirection : 'asc'}
                        onClick={() => handleSort(field)}
                        sx={{ 
                            '& .MuiTableSortLabel-icon': {
                                color: 'inherit !important'
                            }
                        }}
                    >
                        {label}
                    </TableSortLabel>
                ) : label,
                render: (row) => {
                    const value = getNestedValue(row, field);
                    
                    // Handle null/undefined
                    if (value === null || value === undefined) {
                        return (
                            <Typography variant="body2">—</Typography>
                        );
                    }

                    // Combined name - emphasized identity cell
                    if (field === 'full_name') {
                        return (
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {String(value) || '—'}
                            </Typography>
                        );
                    }

                    // Email - LTR, with verified state folded in as an icon
                    if (field === 'email') {
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ direction: 'ltr', textAlign: 'left' }}>
                                    {value}
                                </Typography>
                                {row.isVerified ? (
                                    <CheckCircleIcon sx={{ fontSize: '1rem', color: 'success.main' }} titleAccess="ایمیل تایید شده" />
                                ) : (
                                    <ErrorOutlineIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} titleAccess="ایمیل تایید نشده" />
                                )}
                            </Box>
                        );
                    }

                    // Date fields - format as Jalali
                    if (field === 'birth_date' || field === 'created_date' || field.includes('date') || field.includes('Date')) {
                        const jalaliDate = formatJalaliDate(value);
                        return (
                            <Typography variant="body2">
                                {jalaliDate ? toPersianDigits(jalaliDate) : '—'}
                            </Typography>
                        );
                    }

                    // Email field - preserve LTR direction
                    if (typeof value === 'string' && value.includes('@') && value.includes('.') && !value.includes(' ')) {
                        return (
                            <Typography variant="body2" sx={{ dir: 'ltr', textAlign: 'left' }}>
                                {value}
                            </Typography>
                        );
                    }

                    // isVerified field - use Chip
                    if (field === 'isVerified') {
                        return (
                            <Chip
                                label={value ? 'تایید شده' : 'تایید نشده'}
                                size="small"
                                color={value ? 'success' : 'default'}
                                variant="outlined"
                            />
                        );
                    }

                    // Numbers - display with Persian digits
                    if (typeof value === 'number') {
                        return (
                            <Typography variant="body2">
                                {toPersianDigits(value)}
                            </Typography>
                        );
                    }

                    // Strings with digits - convert to Persian
                    if (typeof value === 'string' && /\d/.test(value)) {
                        return (
                            <Typography variant="body2">
                                {toPersianDigits(value)}
                            </Typography>
                        );
                    }

                    // First name and last name - emphasize with medium weight
                    if (field === 'first_name' || field === 'last_name') {
                        return (
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {String(value)}
                            </Typography>
                        );
                    }

                    // Default: regular text
                    return (
                        <Typography variant="body2">
                            {String(value)}
                        </Typography>
                    );
                }
            };
        });

        // Add actions column at the end
        fieldColumns.push({
            id: 'actions',
            label: 'عملیات',
            compact: true,
            render: (row) => {
                const deleteDisabled = row?.can_delete === false;
                const deleteTitle = deleteDisabled
                    ? 'این هنرجو به دلیل داشتن عملیات قابل حذف نیست.'
                    : 'حذف کاربر';
                return (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(row);
                        }}
                        size="small"
                        color="primary"
                        aria-label="ویرایش کاربر"
                        title="ویرایش کاربر"
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!deleteDisabled) handleDelete(row.id);
                        }}
                        size="small"
                        color="error"
                        aria-label="حذف کاربر"
                        title={deleteTitle}
                        disabled={deleteDisabled}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                    </Box>
                );
            }
        });

        return fieldColumns;
    }, [displayFields, rowHeaders, secondaryFields, handleEdit, handleDelete, orderBy, orderDirection, handleSort]);

    // Memoize row props getter with visual feedback for selected row
    const getRowProps = useCallback((row) => {
        const isSelected = selectedRowId === row.id;
        return {
            selected: isSelected,
            sx: isSelected ? {
                backgroundColor: 'action.selected',
            } : undefined
        };
    }, [selectedRowId]);

    // Handle snackbar close
    const handleSnackbarClose = useCallback((_event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(prev => ({ ...prev, open: false }));
    }, []);

    // Memoize dialog close handlers
    const handleDeleteDialogClose = useCallback(() => {
        setOpenDeleteDialog(false);
        setItemToDelete(null);
    }, []);

    // === Render ===

    return (
        <RTL>
            {showForm ? (
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                        <Button startIcon={<BackIcon />} variant="outlined" onClick={closeForm}>
                            بازگشت به فهرست
                        </Button>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            {isAdding ? (addLabel || 'افزودن') : (formUser?.full_name || title)}
                        </Typography>
                        {!isAdding && formUser && (
                            <Button
                                startIcon={<DeleteIcon />}
                                variant="outlined"
                                color="error"
                                sx={{ ml: 'auto' }}
                                disabled={formUser.can_delete === false}
                                onClick={() => handleDelete(formUser.id)}
                            >
                                حذف
                            </Button>
                        )}
                    </Box>
                    <Paper sx={{ p: 3 }}>
                        <UserForm
                            user={isAdding ? null : formUser}
                            editFields={editFields}
                            fetchUrl={fetchUrl}
                            submitUrl={submitUrl}
                            onSuccess={handleFormSuccess}
                            onCancel={closeForm}
                        />
                    </Paper>
                </Box>
            ) : (
            <Box>
                <Paper sx={{ p: 3, mb: 3 }}>
                    {/* Single toolbar row: title + count · search · status · primary action */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            {icon && <Box sx={{ display: 'flex', color: 'primary.main' }}>{icon}</Box>}
                            <Typography variant="h6" sx={{ m: 0, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                {title}
                            </Typography>
                            {paginatedResponse?.count > 0 && (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    label={toPersianDigits(paginatedResponse.count)}
                                />
                            )}
                        </Box>

                        <Box sx={{ flex: '1 1 220px', minWidth: 200 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="جستجو"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: FIELD_HEIGHT },
                                }}
                            />
                        </Box>

                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel id="user-status-filter-label">وضعیت</InputLabel>
                            <Select
                                labelId="user-status-filter-label"
                                value={statusFilter}
                                label="وضعیت"
                                onChange={(e) => {
                                    setStatusFilter(e.target.value);
                                    setPage(0);
                                }}
                                sx={{ height: FIELD_HEIGHT }}
                            >
                                <MenuItem value="active">فعال</MenuItem>
                                <MenuItem value="inactive">غیرفعال</MenuItem>
                                <MenuItem value="all">همه</MenuItem>
                            </Select>
                        </FormControl>

                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAdd}
                            sx={{ whiteSpace: 'nowrap', height: FIELD_HEIGHT, m: 0 }}
                        >
                            {addLabel}
                        </Button>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            خطا در بارگذاری داده‌ها: {error.message || error}
                        </Alert>
                    )}

                    <StandardTable
                        columns={columns}
                        rows={rows}
                        loading={loading}
                        error={error}
                        emptyText="هیچ موردی یافت نشد."
                        getRowKey={(r) => r.id}
                        ariaLabel="general-list"
                        minWidth={480}
                        getRowProps={getRowProps}
                        onRowClick={handleRowClick}
                    />

                    {paginatedResponse && (
                        <TablePagination
                            totalCount={paginatedResponse.count || 0}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    )}
                </Paper>
            </Box>
            )}

            <ConfirmationDialog
                open={openDeleteDialog}
                onClose={handleDeleteDialogClose}
                onConfirm={confirmDelete}
                title="تأیید حذف"
                message="آیا مطمئن هستید که می‌خواهید این مورد را حذف کنید؟"
                confirmColor="error"
                isLoading={isDeleting}
            />

            <FloatingSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleSnackbarClose}
                anchor={{ vertical: 'bottom', horizontal: 'left' }}
                autoHideDuration={6000}
            />
        </RTL>
    );
}

GeneralList.propTypes = {
    fetchUrl: PropTypes.string.isRequired,            // e.g. "/accounts/api/v1/profile/students/"
    submitUrl: PropTypes.string,                      // optional; will default to fetchUrl
    createDataFunction: PropTypes.func.isRequired,    // must expose .id and (for profiles) .user.id
    rowHeaders: PropTypes.arrayOf(PropTypes.string).isRequired,
    editFields: PropTypes.array.isRequired,
    displayFields: PropTypes.arrayOf(PropTypes.string).isRequired,
    secondaryFields: PropTypes.arrayOf(PropTypes.string), // display fields that fold away on narrow widths
    title: PropTypes.string,                          // per-list heading, e.g. "آموزگاران"
    addLabel: PropTypes.string,                       // per-list add button, e.g. "افزودن آموزگار"
    icon: PropTypes.node,                             // leading icon next to the title
};
